/**
 * @fileoverview SuperAdminCrm — the CRM operations page rendered inside the
 * SuperAdmin shell when `activeTab === 'crm'` (Operations → CRM).
 *
 * Layout (single-column):
 *   1. CrmHealthTile      — health probe + env context, auto-polls every 30s.
 *   2. CrmActionsBar      — Backfill / Reconcile / Cleanup orphans.
 *   3. CrmMappingsTable   — per-entity tallies (Total / Synced / Un-mapped / Failing / Last).
 *   4. Two-column split   — CrmFailuresPanel (left) + CrmAuditPanel (right).
 *
 * The page owns all of the async orchestration (four parallel GETs on mount,
 * a 30 s health poll, mutating actions, per-row retry, and the mappings-cell
 * drilldown drawer). Its five child components are presentational.
 *
 * A single top-level inline error banner surfaces any API failure (with a
 * Retry button that re-fires the full parallel fetch). Toasts communicate
 * transient results from mutating actions.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import Toast from '../components/Toast';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import CrmHealthTile from '../components/CrmHealthTile';
import CrmMappingsTable from '../components/CrmMappingsTable';
import CrmActionsBar from '../components/CrmActionsBar';
import CrmFailuresPanel from '../components/CrmFailuresPanel';
import CrmAuditPanel from '../components/CrmAuditPanel';

/** How often the health tile auto-refreshes while the page is mounted. */
const HEALTH_POLL_MS = 30_000;

/** Fetch limits used for the recent-failures and recent-audit panels. */
const FAILURES_LIMIT = 50;
const AUDIT_LIMIT = 50;

/**
 * Best-effort tally summary for a bulk action toast.
 *
 * @param {Object} payload - Server response body (backfill/reconcile/cleanup).
 * @returns {string} A short "n users, n orgs..." style string; empty when
 *   the payload doesn't carry a recognizable tally.
 */
function summarizeTally(payload) {
  if (!payload || typeof payload !== 'object') return '';
  const tally = payload.tally || payload.resync;
  if (tally && typeof tally === 'object') {
    const parts = Object.entries(tally)
      .filter(([, v]) => Number(v) > 0)
      .map(([k, v]) => `${v} ${k}`);
    if (parts.length) return parts.join(', ');
  }
  if (typeof payload.orphans_removed === 'number') {
    const failed = Number(payload.orphans_failed || 0);
    const removed = Number(payload.orphans_removed || 0);
    if (removed === 0 && failed === 0) return 'no orphans found';
    return failed > 0
      ? `${removed} removed · ${failed} failed`
      : `${removed} removed`;
  }
  return '';
}

/**
 * Extract a short human-readable message from an axios / fetch failure.
 * Falls back to the exception message.
 *
 * @param {Error} err - The thrown error.
 * @returns {string}
 */
function errMessage(err) {
  if (!err) return 'Unknown error';
  const data = err.response && err.response.data;
  if (data) {
    if (typeof data === 'string') return data;
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
  }
  return err.message || String(err);
}

/**
 * Build the "envInfo" object CrmHealthTile expects from a /crm/health payload.
 * The backend does not currently return an API URL or name suffix — those are
 * server-side env vars — so we surface just the enabled flag and let the tile
 * render "—" for the missing fields.
 *
 * @param {Object} healthPayload - Response body from GET /crm/health.
 * @returns {{apiUrl:string, nameSuffix:string, enabled:boolean}}
 */
function deriveEnvInfo(healthPayload) {
  return {
    apiUrl: (healthPayload && healthPayload.api_url) || '',
    nameSuffix: (healthPayload && healthPayload.name_suffix) || '',
    enabled: !!(healthPayload && healthPayload.enabled),
  };
}

/**
 * Ordered list of entity types the mappings table renders. Kept aligned with
 * the backend's _VALID_ENTITY_TYPES tuple in routes/admin_crm.py so the summary
 * columns always show every type — even the ones with a zero mapping count.
 */
const MAPPING_ENTITY_TYPES = ['user', 'organization', 'business', 'vendor'];

/**
 * Roll the /crm/health.mappings tally up with the "failing" figures pulled
 * from a /crm/failures scan so CrmMappingsTable can render every column.
 *
 * The backend's /crm/health returns `mappings: {entity_type: crm_sync_count}`
 * — the raw count of crm_sync rows for that entity type. That includes rows
 * with a recorded last_error, so we derive the "synced" number as
 * `crm_sync_count - failing_count` to avoid double-counting the failing rows.
 *
 * `unmapped` is not derivable from this endpoint alone — the drilldown
 * drawer's per-cell click fetches the authoritative unmapped list via
 * GET /crm/mappings?entity_type=X&status=unmapped, and the summary keeps a
 * conservative zero.
 *
 * @param {Object} mappingCounts - `mappings` slice of /crm/health response.
 * @param {Array<Object>} failures - Rows from /crm/failures.
 * @returns {Object} Tallies keyed by entity_type.
 */
function buildTallies(mappingCounts, failures) {
  const failingByType = {};
  let mostRecentSync = null;
  (Array.isArray(failures) ? failures : []).forEach((row) => {
    const t = row && row.entity_type;
    if (!t) return;
    failingByType[t] = (failingByType[t] || 0) + 1;
  });

  const counts = mappingCounts && typeof mappingCounts === 'object' ? mappingCounts : {};
  const result = {};
  MAPPING_ENTITY_TYPES.forEach((et) => {
    const total = Number(counts[et] || 0);
    const failing = failingByType[et] || 0;
    result[et] = {
      total,
      synced: Math.max(0, total - failing),
      unmapped: 0,
      failing,
      last_sync_at: mostRecentSync,
    };
  });
  return result;
}

/**
 * SuperAdminCrm — Operations → CRM panel.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix (e.g. '/api/{secret}/admin').
 * @returns {JSX.Element}
 */
function SuperAdminCrm({ adminApiPrefix }) {
  const [loading, setLoading] = useState(true);
  const [errorBanner, setErrorBanner] = useState(null);
  const [health, setHealth] = useState(null);
  const [envInfo, setEnvInfo] = useState({ apiUrl: '', nameSuffix: '', enabled: false });
  const [lastProbeAt, setLastProbeAt] = useState(null);
  const [cacheAgeSec, setCacheAgeSec] = useState(0);
  const [mappingCounts, setMappingCounts] = useState({});
  const [failures, setFailures] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [running, setRunning] = useState(null); // 'backfill'|'reconcile'|'cleanup'|null
  const [refreshingHealth, setRefreshingHealth] = useState(false);
  const [retrying, setRetrying] = useState(() => new Set());
  const [toast, setToast] = useState(null); // {message, type}
  const [drawer, setDrawer] = useState(null); // {entityType, status, rows, loading, error}

  // Ref used so the auto-poll's setInterval callback always sees the latest
  // adminApiPrefix without re-registering the interval on unrelated re-renders.
  const prefixRef = useRef(adminApiPrefix);
  useEffect(() => {
    prefixRef.current = adminApiPrefix;
  }, [adminApiPrefix]);

  /**
   * Fire the four page GETs in parallel and hydrate state.
   * Clears any prior error banner on success.
   *
   * @returns {Promise<void>}
   */
  const loadAll = useCallback(async () => {
    if (!adminApiPrefix) return;
    setLoading(true);
    setErrorBanner(null);
    try {
      const [hRes, mRes, fRes, aRes] = await Promise.all([
        api.crmHealth(adminApiPrefix),
        api.crmMappings(adminApiPrefix, { status: 'synced' }),
        api.crmFailures(adminApiPrefix, FAILURES_LIMIT),
        api.crmAudit(adminApiPrefix, AUDIT_LIMIT),
      ]);
      const hd = (hRes && hRes.data) || {};
      setHealth(hd.health || null);
      setEnvInfo({ ...deriveEnvInfo(hd), enabled: !!hd.enabled });
      setMappingCounts((hd && hd.mappings) || {});
      setLastProbeAt(new Date().toISOString());
      setCacheAgeSec(0);
      // /crm/mappings?status=synced is retained here for future drilldown /
      // last-sync-timestamp use; the summary numbers come from hd.mappings
      // (authoritative per-type count) minus the failing tally.
      void (mRes && mRes.data);
      setFailures(((fRes && fRes.data && fRes.data.rows) || []));
      setAuditRows(((aRes && aRes.data && aRes.data.rows) || []));
    } catch (err) {
      setErrorBanner(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [adminApiPrefix]);

  // Initial load — fire the 4 GETs in parallel.
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /**
   * Refresh only the health tile — used both by the manual Refresh button
   * (`?probe=1` to bypass the shared health cache) and the 30 s poll (no probe).
   *
   * @param {boolean} probe - When true, force a live probe.
   * @returns {Promise<void>}
   */
  const refreshHealth = useCallback(async (probe = false) => {
    const prefix = prefixRef.current;
    if (!prefix) return;
    setRefreshingHealth(!!probe);
    try {
      const res = await api.crmHealth(prefix, probe);
      const hd = (res && res.data) || {};
      setHealth(hd.health || null);
      setEnvInfo({ ...deriveEnvInfo(hd), enabled: !!hd.enabled });
      setMappingCounts((hd && hd.mappings) || {});
      setLastProbeAt(new Date().toISOString());
      setCacheAgeSec(0);
    } catch (err) {
      // Don't clobber the whole page on a poll failure — surface it as a toast.
      setToast({ message: `CRM health probe failed: ${errMessage(err)}`, type: 'error' });
    } finally {
      setRefreshingHealth(false);
    }
  }, []);

  // Health auto-poll (30 s). Ticks cacheAgeSec every second so the tile shows
  // an accurate "cached Xs ago" line without a second setInterval.
  useEffect(() => {
    if (!adminApiPrefix) return undefined;
    const pollTimer = setInterval(() => {
      refreshHealth(false);
    }, HEALTH_POLL_MS);
    const ageTimer = setInterval(() => {
      setCacheAgeSec((prev) => prev + 1);
    }, 1000);
    return () => {
      clearInterval(pollTimer);
      clearInterval(ageTimer);
    };
  }, [adminApiPrefix, refreshHealth]);

  /**
   * Wrap the three bulk actions in a common try/finally that flips `running`,
   * shows a tally toast, and refetches mappings / audit / failures so the
   * page reflects the new state.
   *
   * @param {'backfill'|'reconcile'|'cleanup'} key - Which action fired.
   * @param {Function} apiCall - The api.crm* function to invoke.
   * @param {string} verb - Human-readable verb for the success toast.
   * @returns {Promise<void>}
   */
  const runBulkAction = useCallback(async (key, apiCall, verb) => {
    if (running) return;
    setRunning(key);
    try {
      const res = await apiCall(adminApiPrefix);
      const summary = summarizeTally((res && res.data) || {});
      setToast({
        message: summary ? `${verb} complete — ${summary}.` : `${verb} complete.`,
        type: 'success',
      });
      // Refetch downstream state so tallies / audit / failures reflect it.
      try {
        const [hRes, fRes, aRes] = await Promise.all([
          api.crmHealth(adminApiPrefix),
          api.crmFailures(adminApiPrefix, FAILURES_LIMIT),
          api.crmAudit(adminApiPrefix, AUDIT_LIMIT),
        ]);
        const hd = (hRes && hRes.data) || {};
        setHealth(hd.health || null);
        setEnvInfo({ ...deriveEnvInfo(hd), enabled: !!hd.enabled });
        setMappingCounts((hd && hd.mappings) || {});
        setLastProbeAt(new Date().toISOString());
        setCacheAgeSec(0);
        setFailures(((fRes && fRes.data && fRes.data.rows) || []));
        setAuditRows(((aRes && aRes.data && aRes.data.rows) || []));
      } catch (_refetchErr) {
        // Refetch failure is non-fatal — the action itself already ran.
        setToast({
          message: `${verb} completed but refresh failed. Reload to see updated tallies.`,
          type: 'warning',
        });
      }
    } catch (err) {
      setToast({ message: `${verb} failed: ${errMessage(err)}`, type: 'error' });
    } finally {
      setRunning(null);
    }
  }, [adminApiPrefix, running]);

  /**
   * Retry a single failed sync row.
   *
   * @param {string} entityType
   * @param {number} entityId
   * @returns {Promise<void>}
   */
  const handleRetry = useCallback(async (entityType, entityId) => {
    if (!adminApiPrefix) return;
    const key = `${entityType}:${entityId}`;
    setRetrying((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      const res = await api.crmResync(adminApiPrefix, entityType, entityId);
      const action = (res && res.data && res.data.action) || 'synced';
      setToast({
        message: `Resync ${entityType} #${entityId} — ${action}.`,
        type: 'success',
      });
      // Refetch the failures list so cleared rows drop out.
      try {
        const fRes = await api.crmFailures(adminApiPrefix, FAILURES_LIMIT);
        setFailures(((fRes && fRes.data && fRes.data.rows) || []));
      } catch (_refetchErr) {
        // Non-fatal — leave existing list in place.
      }
    } catch (err) {
      setToast({ message: `Resync failed: ${errMessage(err)}`, type: 'error' });
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [adminApiPrefix]);

  /**
   * Stub for the "File as feedback" button. Phase 3 wires this to the feedback
   * modal; for now, drop a toast so the operator knows the button hit.
   *
   * @param {Object} failureRow - The full failure row from CrmFailuresPanel.
   */
  const handleFileAsFeedback = useCallback((failureRow) => {
    const et = failureRow && failureRow.entity_type;
    const id = failureRow && failureRow.entity_id;
    setToast({
      message: `File-as-feedback for ${et} #${id} ships in the feedback linking phase.`,
      type: 'info',
    });
  }, []);

  /**
   * Open the mappings drilldown drawer for a specific (entity_type, status)
   * cell and load the underlying rows.
   *
   * @param {string} entityType
   * @param {'unmapped'|'failing'} status
   * @returns {Promise<void>}
   */
  const openMappingsDrawer = useCallback(async (entityType, status) => {
    setDrawer({ entityType, status, rows: [], loading: true, error: null });
    try {
      const res = await api.crmMappings(adminApiPrefix, {
        entity_type: entityType,
        status,
      });
      const rows = (res && res.data && res.data.rows) || [];
      setDrawer({ entityType, status, rows, loading: false, error: null });
    } catch (err) {
      setDrawer({
        entityType,
        status,
        rows: [],
        loading: false,
        error: errMessage(err),
      });
    }
  }, [adminApiPrefix]);

  /**
   * Retry a row from inside the drawer. On success, drop the row from the
   * drawer's list so the operator can visually chew through the backlog.
   *
   * @param {string} entityType
   * @param {number} entityId
   * @returns {Promise<void>}
   */
  const handleDrawerRetry = useCallback(async (entityType, entityId) => {
    await handleRetry(entityType, entityId);
    setDrawer((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.filter((r) => r.entity_id !== entityId || r.entity_type !== entityType),
      };
    });
  }, [handleRetry]);

  const tallies = useMemo(
    () => buildTallies(mappingCounts, failures),
    [mappingCounts, failures]
  );

  return (
    <div
      role="tabpanel"
      aria-labelledby="tab-crm"
      className="superadmin-crm"
      data-testid="superadmin-crm-page"
    >
      <header className="superadmin-crm__header">
        <h2 className="superadmin-crm__title">CRM (Twenty) Operations</h2>
        <p className="superadmin-crm__sub">
          Live health, mappings, and manual controls for the CRM sync pipeline.
        </p>
      </header>

      {errorBanner && (
        <div
          className="superadmin-crm__error"
          role="alert"
          data-testid="superadmin-crm-error"
        >
          <span className="superadmin-crm__error-msg">
            Failed to load CRM ops data: {errorBanner}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={loadAll}
            aria-label="Retry loading CRM ops data"
            data-testid="superadmin-crm-error-retry"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !health ? (
        <Loading variant="spinner" message="Loading CRM operations…" />
      ) : (
        <>
          <CrmHealthTile
            health={health}
            envInfo={envInfo}
            lastProbe={lastProbeAt}
            cacheAgeSec={cacheAgeSec}
            onRefresh={() => refreshHealth(true)}
            loading={refreshingHealth}
          />

          <CrmActionsBar
            onBackfill={() => runBulkAction('backfill', api.crmBackfill, 'Backfill')}
            onReconcile={() => runBulkAction('reconcile', api.crmReconcile, 'Reconcile')}
            onCleanup={() => runBulkAction('cleanup', api.crmCleanupOrphans, 'Cleanup')}
            running={running}
            disabled={!envInfo.enabled}
          />

          <CrmMappingsTable
            tallies={tallies}
            onRowClick={openMappingsDrawer}
          />

          <div className="superadmin-crm__split">
            <section
              className="superadmin-crm__panel"
              aria-label="Recent CRM sync failures"
            >
              <h3 className="superadmin-crm__panel-title">Recent sync failures</h3>
              <CrmFailuresPanel
                failures={failures}
                onRetry={handleRetry}
                onFileAsFeedback={handleFileAsFeedback}
                retrying={retrying}
              />
            </section>
            <section
              className="superadmin-crm__panel"
              aria-label="Recent CRM operator actions"
            >
              <h3 className="superadmin-crm__panel-title">Recent operator actions</h3>
              <CrmAuditPanel auditRows={auditRows} />
            </section>
          </div>
        </>
      )}

      {drawer && (
        <Modal
          isOpen={true}
          onClose={() => setDrawer(null)}
          title={`${drawer.status === 'unmapped' ? 'Un-mapped' : 'Failing'} ${drawer.entityType}s`}
          subtitle={
            drawer.status === 'failing'
              ? 'Rows with a recorded last_error. Retry to re-run the sync.'
              : 'Live rows with no crm_sync mapping. Retry to POST them to Twenty.'
          }
          size="large"
          className="superadmin-crm__drawer"
        >
          {drawer.loading ? (
            <Loading variant="spinner" message="Loading rows…" />
          ) : drawer.error ? (
            <div
              className="superadmin-crm__drawer-error"
              role="alert"
              data-testid="superadmin-crm-drawer-error"
            >
              {drawer.error}
            </div>
          ) : drawer.rows.length === 0 ? (
            <div className="empty-state" data-testid="superadmin-crm-drawer-empty">
              <h3 className="empty-state__title">Nothing here.</h3>
              <p className="empty-state__description">
                No {drawer.status} rows for this entity type right now.
              </p>
            </div>
          ) : (
            <div
              className="tbl-wrap"
              data-testid="superadmin-crm-drawer-table"
            >
              <table>
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Name</th>
                    {drawer.status === 'failing' && (
                      <th scope="col">Last error</th>
                    )}
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {drawer.rows.map((row) => {
                    const key = `${row.entity_type}:${row.entity_id}`;
                    const isRetrying = retrying.has(key);
                    return (
                      <tr
                        key={key}
                        data-testid={`superadmin-crm-drawer-row-${row.entity_type}-${row.entity_id}`}
                      >
                        <td className="td-mono">#{row.entity_id}</td>
                        <td>{row.display_name || '—'}</td>
                        {drawer.status === 'failing' && (
                          <td title={row.last_error || undefined}>
                            {(row.last_error || '').slice(0, 80)}
                            {row.last_error && row.last_error.length > 80 ? '…' : ''}
                          </td>
                        )}
                        <td>
                          <button
                            type="button"
                            className={`btn btn-sm btn-secondary${isRetrying ? ' btn-loading' : ''}`}
                            onClick={() => handleDrawerRetry(row.entity_type, row.entity_id)}
                            disabled={isRetrying}
                            aria-label={`Resync ${row.entity_type} #${row.entity_id}`}
                            data-testid={`superadmin-crm-drawer-retry-${row.entity_type}-${row.entity_id}`}
                          >
                            {isRetrying ? 'Retrying…' : 'Resync'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SuperAdminCrm;
